import React, { useState } from "react";
import {
  Typography,
  Card,
  DatePicker,
  Button,
  Select,
  Flex,
  Row,
  Col,
  Skeleton,
} from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  useReportsServiceDownloadProfitReportV2,
  useReportsServiceGetProfitReportV2,
} from "api/queries";

const { Title, Text } = Typography;

export const ProfitAndLossReportV2 = () => {
  const company = useSelector((state: any) => state.company);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);
  const [periodFilter, setPeriodFilter] = useState("Today");

  const { data: reportData, isLoading } = useReportsServiceGetProfitReportV2({
    startDate: dateRange[0].toISOString(),
    endDate: dateRange[1].toISOString(),
  });

  const { mutate: downloadReport, isPending } =
    useReportsServiceDownloadProfitReportV2();

  // Use actual API data with fallback values
  const data = reportData || {
    kpis: {
      totalIncome: 0,
      totalCogs: 0,
      grossProfit: 0,
      operatingProfit: 0,
      netProfit: 0,
      productSales: 0,
      reparationSales: 0,
      productCost: 0,
      repairParts: 0,
      profitMarginPercentage: 0,
    },
    operatingExpenses: [],
  };

  // Handle period filter change
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();

    switch (value) {
      case "Today":
        setDateRange([now.startOf("day"), now.endOf("day")]);
        break;
      case "This Week":
        setDateRange([now.startOf("week"), now.endOf("week")]);
        break;
      case "This Month":
        setDateRange([now.startOf("month"), now.endOf("month")]);
        break;
      case "This Year":
        setDateRange([now.startOf("year"), now.endOf("year")]);
        break;
      default:
        setDateRange([now.startOf("day"), now.endOf("day")]);
    }
  };

  const handleDownload = () => {
    downloadReport(
      {
        startDate: dateRange[0].format("YYYY-MM-DDTHH:mm:ss"),
        endDate: dateRange[1].format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: { downloadUrl?: string }) => {
          if (response?.downloadUrl) {
            const link = document.createElement("a");
            link.href = response.downloadUrl;
            link.setAttribute("download", "profit_and_loss_report.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      }
    );
  };

  const formatCurrency = (value: number) => {
    return `${company.currencySymbol}${value.toLocaleString()}`;
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 20 }}>
        <Title level={2} style={{ margin: 0 }}>
          Profit & Loss Report
        </Title>
        <div style={{ display: "flex", gap: 10 }}>
          <Select
            value={periodFilter}
            onChange={handlePeriodChange}
            style={{ width: 120 }}
          >
            <Select.Option value="Today">Today</Select.Option>
            <Select.Option value="This Week">This Week</Select.Option>
            <Select.Option value="This Month">This Month</Select.Option>
            <Select.Option value="This Year">This Year</Select.Option>
          </Select>
          <DatePicker
            value={dateRange[0]}
            onChange={(date) => setDateRange([date || dayjs(), dateRange[1]])}
            style={{ width: 140 }}
            placeholder="From"
          />
          <DatePicker
            value={dateRange[1]}
            onChange={(date) => setDateRange([dateRange[0], date || dayjs()])}
            style={{ width: 140 }}
            placeholder="To"
          />
          <Button
            type="primary"
            style={{ background: "#fa8c16", borderColor: "#fa8c16" }}
            onClick={handleDownload}
            loading={isPending}
          >
            Export Report
          </Button>
        </div>
      </Flex>

      {/* KPI Cards */}
      <Row gutter={16} justify="space-between" style={{ marginBottom: 24 }}>
        {isLoading ? (
          Array.from({ length: 5 }).map((_, idx) => (
            <Col span={4.8} style={{ minWidth: "200px" }} key={idx}>
              <Skeleton
                active
                paragraph={false}
                title={{ width: "80%" }}
                style={{ padding: 25, borderRadius: 8 }}
              />
            </Col>
          ))
        ) : (
          <>
            <Col span={4.8} style={{ minWidth: "200px" }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "8px",
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                  borderLeft: "4px solid #fa8c16",
                  width: "100%",
                }}
              >
                <div style={{ color: "#8c8c8c", marginBottom: "8px" }}>
                  Total Income
                </div>
                <div
                  style={{
                    color: "#fa8c16",
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginTop: "18px",
                  }}
                >
                  {formatCurrency(data.kpis.totalIncome)}
                </div>
              </div>
            </Col>
            <Col span={4.8} style={{ minWidth: "200px" }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "8px",
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                  borderLeft: "4px solid #fa8c16",
                  width: "100%",
                }}
              >
                <div style={{ color: "#8c8c8c", marginBottom: "8px" }}>
                  Total COGS
                </div>
                <div
                  style={{
                    color: "#fa8c16",
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginTop: "18px",
                  }}
                >
                  {formatCurrency(data.kpis.totalCogs)}
                </div>
              </div>
            </Col>
            <Col span={4.8} style={{ minWidth: "200px" }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "8px",
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                  borderLeft: "4px solid #fa8c16",
                  width: "100%",
                }}
              >
                <div style={{ color: "#8c8c8c", marginBottom: "8px" }}>
                  Gross Profit
                </div>
                <div
                  style={{
                    color: "#fa8c16",
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginTop: "18px",
                  }}
                >
                  {formatCurrency(data.kpis.grossProfit)}
                </div>
              </div>
            </Col>
            <Col span={4.8} style={{ minWidth: "200px" }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "8px",
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                  borderLeft: "4px solid #fa8c16",
                  width: "100%",
                }}
              >
                <div style={{ color: "#8c8c8c", marginBottom: "8px" }}>
                  Operating Profit
                </div>
                <div
                  style={{
                    color: "#fa8c16",
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginTop: "18px",
                  }}
                >
                  {formatCurrency(data.kpis.operatingProfit)}
                </div>
              </div>
            </Col>
            <Col span={4.8} style={{ minWidth: "200px" }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "8px",
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                  borderLeft: "4px solid #fa8c16",
                  width: "100%",
                }}
              >
                <div style={{ color: "#8c8c8c", marginBottom: "8px" }}>
                  Net Profit
                </div>
                <div
                  style={{
                    color: "#fa8c16",
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginTop: "18px",
                  }}
                >
                  {formatCurrency(data.kpis.netProfit)}
                </div>
              </div>
            </Col>
          </>
        )}
      </Row>

      {/* Main Report Sections */}
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : (
        <>
          {/* Income Section */}
          <Card
            style={{
              marginBottom: 16,
              borderLeft: "4px solid #fa8c16",
            }}
            bodyStyle={{ padding: "12px 16px" }}
          >
            <Title level={4} style={{ marginBottom: 12 }}>
              Income
            </Title>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Col>
                <Text>Product Sales</Text>
              </Col>
              <Col>
                <Text strong>{formatCurrency(data.kpis.productSales)}</Text>
              </Col>
            </Row>
            <Row justify="space-between">
              <Col>
                <Text>Reparation sales</Text>
              </Col>
              <Col>
                <Text strong>{formatCurrency(data.kpis.reparationSales)}</Text>
              </Col>
            </Row>
          </Card>

          {/* COGS Section */}
          <Card
            style={{
              marginBottom: 16,
              borderLeft: "4px solid #fa8c16",
            }}
            bodyStyle={{ padding: "12px 16px" }}
          >
            <Title level={4} style={{ marginBottom: 12 }}>
              Cost of Goods Sold (COGS)
            </Title>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Col>
                <Text>Product Cost</Text>
              </Col>
              <Col>
                <Text strong>{formatCurrency(data.kpis.productCost)}</Text>
              </Col>
            </Row>
            <Row justify="space-between">
              <Col>
                <Text>Repair Parts</Text>
              </Col>
              <Col>
                <Text strong>{formatCurrency(data.kpis.repairParts)}</Text>
              </Col>
            </Row>
          </Card>

          {/* Gross Profit */}
          <div
            style={{
              background: "#f5f5f5",
              padding: "12px 16px",
              borderRadius: 8,
              marginBottom: 20,
            }}
          >
            <Row justify="space-between">
              <Col>
                <Title level={4} style={{ margin: 0 }}>
                  Gross Profit: {formatCurrency(data.kpis.grossProfit)}{" "}
                  <Text
                    type="secondary"
                    style={{ fontSize: 14, fontWeight: 400 }}
                  >
                    ({formatPercentage(data.kpis.profitMarginPercentage)}{" "}
                    margin)
                  </Text>
                </Title>
              </Col>
            </Row>
          </div>

          {/* Operating Expenses */}
          <Card
            style={{
              marginBottom: 16,
              borderLeft: "4px solid #fa8c16",
            }}
            bodyStyle={{ padding: "12px 16px" }}
          >
            <Title level={4} style={{ marginBottom: 12 }}>
              Operating Expenses (OPEX)
            </Title>
            {data.operatingExpenses.map((expense, index) => (
              <Row
                justify="space-between"
                style={{ marginBottom: 8 }}
                key={index}
              >
                <Col>
                  <Text>{expense.categoryName}</Text>
                </Col>
                <Col>
                  <Text strong>{formatCurrency(expense.amount)}</Text>
                </Col>
              </Row>
            ))}
            {data.operatingExpenses.length === 0 && (
              <Row justify="space-between" style={{ marginBottom: 8 }}>
                <Col>
                  <Text>No operating expenses</Text>
                </Col>
                <Col>
                  <Text strong>{formatCurrency(0)}</Text>
                </Col>
              </Row>
            )}
          </Card>

          {/* Operating Profit */}
          <div
            style={{
              background: "#f5f5f5",
              padding: "12px 16px",
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <Row justify="space-between">
              <Col>
                <Title level={4} style={{ margin: 0 }}>
                  Operating Profit: {formatCurrency(data.kpis.operatingProfit)}
                </Title>
              </Col>
            </Row>
          </div>

          {/* Final Net Profit */}
          <div
            style={{
              background: "#f5f5f5",
              padding: "12px 16px",
              borderRadius: 8,
            }}
          >
            <Row justify="space-between">
              <Col>
                <Title level={3} style={{ margin: 0, color: "#52c41a" }}>
                  Net Profit: {formatCurrency(data.kpis.netProfit)}
                </Title>
              </Col>
            </Row>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfitAndLossReportV2;
